'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { Check, CreditCard, Lock, Sparkles } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { LoginFormCard } from '@/components/forms/login-form'
import { RegisterFormCard } from '@/components/forms/register-form'
import {
  bootstrapSession,
  exchangeDashboardSession,
  isRateLimitedExchangeError,
  type AppSession,
  type DashboardExchangeError,
} from '@/lib/auth'
import { redirectToDashboard, redirectToStripeIfNeeded } from '@/lib/auth-helpers'
import {
  type BillingPeriod,
  type PlanTier,
  resolveBillingPeriodFromSearchParams,
} from '@/lib/billing'
import { resolvePlanFromSearchParams } from '@/lib/plan'
import { logger } from '@/lib/logger'
import { cn } from '@/lib/utils'
import { PRICING_DEFAULT_PERIOD } from '@/config/pricing'
import {
  buildFallbackCatalog,
  PLAN_ORDER,
  fetchPlanCatalog,
  resolvePlanMetadata,
  type PlanCatalog,
} from './plan-catalog'

const formatBillingPeriod = (billingPeriod: string) => {
  const normalized = billingPeriod.toLowerCase()
  if (normalized === 'mensual' || normalized === 'monthly' || normalized === 'month') return '/mes'
  if (
    normalized === 'anual' ||
    normalized === 'yearly' ||
    normalized === 'annual' ||
    normalized === 'year'
  )
    return '/mes (anual)'
  return `/${billingPeriod}`
}

const isEnterprisePlan = (plan: PlanTier | null): plan is 'enterprise' => plan === 'enterprise'

const buildCheckoutRedirectPath = (plan: PlanTier | null, period?: BillingPeriod | null) => {
  const params = new URLSearchParams()
  if (plan) params.set('plan', plan)
  if (period) params.set('period', period)
  const query = params.toString()
  return query ? `/checkout?${query}` : '/checkout'
}

export function CheckoutFlow({
  initialPlan,
  initialCatalog,
}: {
  initialPlan?: PlanTier | null
  initialCatalog?: PlanCatalog | null
}) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [catalog, setCatalog] = useState<PlanCatalog | null>(initialCatalog ?? null)
  const [catalogState, setCatalogState] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    initialCatalog ? 'ready' : 'loading'
  )
  const [sessionState, setSessionState] = useState<'checking' | 'authenticated' | 'guest'>(
    'checking'
  )
  const [exchangeState, setExchangeState] = useState<'idle' | 'working' | 'error'>('idle')
  const [exchangeError, setExchangeError] = useState<string | null>(null)
  const sessionRef = useRef<AppSession | null>(null)
  const exchangeKeyRef = useRef<string | null>(null)

  const planFromUrl = resolvePlanFromSearchParams(searchParams)
  const billingPeriodFromUrl = resolveBillingPeriodFromSearchParams(searchParams)
  const activePlan = planFromUrl ?? initialPlan ?? null
  const billingPeriod = billingPeriodFromUrl ?? PRICING_DEFAULT_PERIOD
  const resolvedPlan = activePlan
  const checkoutRedirectPath = buildCheckoutRedirectPath(resolvedPlan, billingPeriod)
  const fallbackCatalog = useMemo(() => buildFallbackCatalog(billingPeriod), [billingPeriod])

  const planMetadata = useMemo(
    () => (resolvedPlan ? resolvePlanMetadata(resolvedPlan, catalog, billingPeriod) : null),
    [resolvedPlan, catalog, billingPeriod]
  )

  const planOptions = useMemo(
    () =>
      PLAN_ORDER.map((plan) => ({
        plan,
        metadata: resolvePlanMetadata(plan, catalog, billingPeriod),
      })),
    [catalog, billingPeriod]
  )

  const selectPlan = useCallback(
    (plan: PlanTier) => {
      const params = new URLSearchParams(searchParams?.toString() ?? '')
      params.set('plan', plan)
      params.set('period', billingPeriod)
      router.replace(`/checkout?${params.toString()}`)
    },
    [router, searchParams, billingPeriod]
  )

  const startExchange = useCallback(
    async (session: AppSession, plan: PlanTier) => {
      setExchangeState('working')
      setExchangeError(null)
      try {
        const exchangeResult = await exchangeDashboardSession(session, {
          plan,
          billingPeriod,
        })
        if (redirectToStripeIfNeeded(exchangeResult)) {
          return
        }
        redirectToDashboard((path) => router.replace(path), searchParams.get('next'))
      } catch (error) {
        if (isRateLimitedExchangeError(error)) {
          setExchangeError(error.message)
          setExchangeState('error')
          return
        }
        if (error && typeof error === 'object' && 'message' in error) {
          const err = error as DashboardExchangeError
          setExchangeError(err.message ?? 'No pudimos iniciar el checkout. Intenta nuevamente.')
        } else {
          setExchangeError('No pudimos iniciar el checkout. Intenta nuevamente.')
        }
        setExchangeState('error')
        logger.error('[Checkout] Exchange failed', { error })
      }
    },
    [router, billingPeriod, searchParams]
  )

  useEffect(() => {
    if (initialCatalog) return

    const controller = new AbortController()

    fetchPlanCatalog({ signal: controller.signal, period: billingPeriod })
      .then((data) => {
        setCatalog(data)
        setCatalogState('ready')
      })
      .catch((error) => {
        logger.warn('[Checkout] No pudimos cargar el catálogo de planes, usando fallback', {
          error,
        })
        setCatalog(fallbackCatalog)
        setCatalogState('error')
      })

    return () => {
      controller.abort()
    }
  }, [initialCatalog, billingPeriod, fallbackCatalog])

  useEffect(() => {
    let cancelled = false

    bootstrapSession()
      .then((session) => {
        if (cancelled) return
        sessionRef.current = session
        setSessionState(session ? 'authenticated' : 'guest')
      })
      .catch((error) => {
        if (cancelled) return
        logger.warn('[Checkout] No pudimos validar la sesión de Supabase', { error })
        setSessionState('guest')
      })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!resolvedPlan || !sessionRef.current) return
    if (isEnterprisePlan(resolvedPlan)) return

    const exchangeKey = `${sessionRef.current.tenantId}:${resolvedPlan}:${billingPeriod}`
    if (exchangeKeyRef.current === exchangeKey) return
    exchangeKeyRef.current = exchangeKey

    if (sessionState === 'authenticated') {
      void startExchange(sessionRef.current, resolvedPlan)
    }
  }, [resolvedPlan, sessionState, startExchange, billingPeriod])

  const handleRetry = () => {
    if (!resolvedPlan || !sessionRef.current) return
    exchangeKeyRef.current = null
    void startExchange(sessionRef.current, resolvedPlan)
  }

  if (!resolvedPlan) {
    return (
      <section className="relative overflow-hidden px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl text-center">
          <Badge className="bg-primary/10 text-primary border-primary/15 border">
            Checkout seguro
          </Badge>
          <h1 className="text-foreground mt-4 text-balance text-4xl font-semibold tracking-tight sm:text-5xl">
            Elige el plan que mejor se adapta a tu equipo
          </h1>
          <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-lg">
            Selecciona un plan para continuar con el checkout en Stripe. Puedes cambiarlo cuando
            quieras desde tu panel.
          </p>
        </div>

        <div className="mx-auto mt-12 grid max-w-5xl gap-6 md:grid-cols-3">
          {planOptions.map(({ plan, metadata }) => {
            const highlight = plan === 'professional'
            const hasNumericPrice = /\d/.test(metadata.price)
            const previewFeatures =
              metadata.features.length > 0
                ? metadata.features.slice(0, 3)
                : fallbackCatalog[plan].features.slice(0, 3)
            return (
              <Card
                key={plan}
                className={cn(
                  'border-border/70 bg-card/95 flex h-full flex-col gap-4 rounded-2xl border p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg',
                  highlight && 'border-primary shadow-primary/10'
                )}
              >
                <div className="flex items-center justify-between">
                  <p className="text-primary text-xs font-semibold uppercase tracking-[0.2em]">
                    {metadata.name}
                  </p>
                  {highlight ? (
                    <Badge className="bg-primary text-primary-foreground">Más popular</Badge>
                  ) : null}
                </div>
                <p className="text-foreground/70 text-sm">{metadata.description}</p>
                <div className="mt-2">
                  <p className="text-foreground text-3xl font-semibold">
                    {metadata.price}
                    {hasNumericPrice ? (
                      <span className="text-foreground/60 ml-1 text-sm font-medium">
                        {formatBillingPeriod(metadata.billingPeriod)}
                      </span>
                    ) : null}
                  </p>
                </div>
                <ul className="text-foreground/70 mt-2 space-y-2 text-sm">
                  {previewFeatures.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="text-primary mt-0.5 h-4 w-4" aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button className="mt-auto" onClick={() => selectPlan(plan)}>
                  Elegir plan
                </Button>
              </Card>
            )
          })}
        </div>

        <div className="mt-10 text-center">
          <Link
            href="/#precios"
            className="text-muted-foreground hover:text-foreground text-sm underline-offset-4 hover:underline"
          >
            Volver a precios
          </Link>
        </div>
      </section>
    )
  }

  const hasNumericPrice = planMetadata ? /\d/.test(planMetadata.price) : false
  const resolvedFeatures =
    planMetadata && planMetadata.features.length > 0
      ? planMetadata.features
      : fallbackCatalog[resolvedPlan].features

  return (
    <section className="relative overflow-hidden px-4 py-20 sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-[-30%] h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle_at_center,rgba(14,165,233,0.18),transparent_70%)] blur-3xl" />
        <div className="absolute right-0 top-1/3 h-[360px] w-[360px] rounded-full bg-[radial-gradient(circle_at_center,rgba(14,165,233,0.12),transparent_70%)] blur-3xl" />
      </div>

      <div className="mx-auto mb-12 max-w-5xl text-center">
        <Badge className="bg-primary/10 text-primary border-primary/15 border">
          Checkout en Stripe
        </Badge>
        <h1 className="text-foreground mt-4 text-balance text-4xl font-semibold tracking-tight sm:text-5xl">
          Completa tu suscripción de forma segura
        </h1>
        <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-lg">
          Terminamos la configuración y te llevamos directo a Stripe para finalizar el pago.
        </p>
      </div>

      <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[minmax(0,1fr)_420px]">
        <Card className="border-border/70 bg-card/95 relative overflow-hidden rounded-3xl border p-8 shadow-lg">
          <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-blue-500/10 blur-2xl" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary text-xs font-semibold uppercase tracking-[0.3em]">
                Plan seleccionado
              </p>
              <h2 className="text-foreground mt-2 text-3xl font-semibold">
                {planMetadata?.name ?? 'Plan'}
              </h2>
            </div>
            <Badge className="bg-foreground text-background">Seguro</Badge>
          </div>

          <p className="text-muted-foreground mt-4 text-base">
            {planMetadata?.description ??
              'Tu suscripción se activa apenas completes el pago en Stripe.'}
          </p>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="text-foreground text-4xl font-semibold">
              {planMetadata?.price ?? '—'}
            </span>
            {hasNumericPrice ? (
              <span className="text-muted-foreground text-sm font-medium">
                {formatBillingPeriod(planMetadata?.billingPeriod ?? 'mensual')}
              </span>
            ) : null}
          </div>

          <div className="text-foreground/80 mt-8 grid gap-3 text-sm">
            {resolvedFeatures.map((feature) => (
              <div key={feature} className="flex items-start gap-3">
                <div className="bg-primary/10 text-primary mt-0.5 flex h-6 w-6 items-center justify-center rounded-full">
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>

          <div className="text-muted-foreground mt-8 flex flex-wrap items-center gap-3 text-xs">
            <div className="flex items-center gap-2 rounded-full border border-black/5 bg-white/70 px-3 py-1.5 dark:border-white/10 dark:bg-white/5">
              <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Pago cifrado con Stripe</span>
            </div>
            <div className="flex items-center gap-2 rounded-full border border-black/5 bg-white/70 px-3 py-1.5 dark:border-white/10 dark:bg-white/5">
              <CreditCard className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Tarjetas y transferencias</span>
            </div>
            <div className="flex items-center gap-2 rounded-full border border-black/5 bg-white/70 px-3 py-1.5 dark:border-white/10 dark:bg-white/5">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Prueba gratuita si aplica</span>
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => router.replace(buildCheckoutRedirectPath(null, billingPeriod))}
            >
              Cambiar plan
            </Button>
            <Link
              href="/#precios"
              className="text-muted-foreground hover:text-foreground text-sm underline-offset-4 hover:underline"
            >
              Ver detalles de planes
            </Link>
          </div>
        </Card>

        <Card className="border-border/70 bg-card/95 flex flex-col gap-6 rounded-3xl border p-6 shadow-lg">
          {isEnterprisePlan(resolvedPlan) ? (
            <>
              <div>
                <Badge className="bg-primary/10 text-primary border-primary/15 border">
                  Enterprise
                </Badge>
                <h3 className="text-foreground mt-3 text-2xl font-semibold">
                  Hablemos de tu implementación
                </h3>
                <p className="text-muted-foreground mt-2 text-sm">
                  Cuéntanos tus requerimientos para cotizar un plan Enterprise con SLA y
                  acompañamiento dedicado.
                </p>
              </div>
              <Button asChild className="mt-auto">
                <Link href="/contacto">Contactar ventas</Link>
              </Button>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <div className="flex rounded-full border border-black/5 bg-slate-50 p-1 text-sm font-medium dark:border-white/10 dark:bg-white/5">
                  <button
                    type="button"
                    onClick={() => router.replace(`${checkoutRedirectPath}&mode=register`)}
                    className={cn(
                      'flex-1 rounded-full px-4 py-2 transition-colors focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-emerald-500/40',
                      (searchParams?.get('mode') ?? 'register') === 'register'
                        ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-white'
                        : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white'
                    )}
                    aria-pressed={(searchParams?.get('mode') ?? 'register') === 'register'}
                  >
                    Crear cuenta
                  </button>
                  <button
                    type="button"
                    onClick={() => router.replace(`${checkoutRedirectPath}&mode=login`)}
                    className={cn(
                      'flex-1 rounded-full px-4 py-2 transition-colors focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-emerald-500/40',
                      (searchParams?.get('mode') ?? 'register') === 'login'
                        ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-white'
                        : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white'
                    )}
                    aria-pressed={(searchParams?.get('mode') ?? 'register') === 'login'}
                  >
                    Ya tengo cuenta
                  </button>
                </div>
                <p className="text-muted-foreground text-xs">
                  Conecta tu cuenta y te llevamos directo a Stripe.
                </p>
              </div>

              {sessionState === 'checking' ? (
                <div className="space-y-4">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : sessionState === 'authenticated' ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
                  <div className="bg-primary/10 text-primary flex h-12 w-12 items-center justify-center rounded-full">
                    <CreditCard className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-foreground text-sm font-semibold">
                      {exchangeState === 'working'
                        ? 'Preparando tu checkout…'
                        : 'Continuando con el checkout'}
                    </p>
                    <p className="text-muted-foreground mt-1 text-xs">
                      Te llevaremos a Stripe en unos segundos.
                    </p>
                  </div>
                  {exchangeError ? (
                    <div className="text-destructive text-sm">
                      {exchangeError}
                      <div className="mt-3">
                        <Button size="sm" variant="outline" onClick={handleRetry}>
                          Reintentar
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <>
                  {(searchParams?.get('mode') ?? 'register') === 'login' ? (
                    <LoginFormCard
                      planHint={resolvedPlan}
                      billingPeriodHint={billingPeriod}
                      redirectToCheckout
                      oauthRedirectPath={checkoutRedirectPath}
                    />
                  ) : (
                    <RegisterFormCard
                      planHint={resolvedPlan}
                      billingPeriodHint={billingPeriod}
                      redirectToCheckout
                      oauthRedirectPath={checkoutRedirectPath}
                    />
                  )}
                </>
              )}

              <div className="text-muted-foreground mt-auto text-xs">
                Al continuar aceptas los{' '}
                <Link href="/terms" className="text-foreground underline-offset-4 hover:underline">
                  términos
                </Link>{' '}
                y{' '}
                <Link
                  href="/privacy"
                  className="text-foreground underline-offset-4 hover:underline"
                >
                  privacidad
                </Link>
                .
              </div>
            </>
          )}
        </Card>
      </div>

      {catalogState === 'error' ? (
        <p className="text-muted-foreground mt-10 text-center text-xs">
          No pudimos cargar el catálogo de Stripe. Mostramos valores de referencia.
        </p>
      ) : null}
    </section>
  )
}
